import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Box, 
    Typography, 
    CircularProgress, 
    Alert,
    IconButton,
    Chip
} from '@mui/material';
import { 
    Favorite, 
    FavoriteBorder, 
    ArrowBack
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { userRequest } from '../../requestMethods';
import { AlgorithmicProblemInteractiveTextViewer } from '../InteractiveText';
import { TappedWordsWidget } from '../Learning';
import TaskAnswerViewer from './TaskAnswerViewer';
import PropTypes from 'prop-types';
import styles from '../InteractiveText/AlgorithmicProblem.module.css';

const AlgorithmicProblemView = ({ problemId: propProblemId }) => {
    const { id: paramProblemId } = useParams();
    const problemId = propProblemId || paramProblemId;
    const navigate = useNavigate();
    
    const [problem, setProblem] = useState(null);
    const [formattedTaskText, setFormattedTaskText] = useState(null);
    const [solutions, setSolutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isFavorite, setIsFavorite] = useState(false);
    const [favoriteLoading, setFavoriteLoading] = useState(false);
    const [tappedWords, setTappedWords] = useState([]);

    const fetchProblem = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch problem data with populated task and solutions
            const problemResponse = await userRequest.get(`/algorithmic-problem/${problemId}`);
            const problemData = problemResponse.data;
            setProblem(problemData);

            // Fetch formatted text using the text ID (Task)
            if (problemData.task?._id) {
                const taskResponse = await userRequest.get(`/text/formatted/byText/${problemData.task._id}?problemId=${problemId}`);
                setFormattedTaskText(taskResponse.data);
            }

            // Handle solutions - always fetch formatted text, fallback to pure text if formatted doesn't exist
            if (problemData.solutions && problemData.solutions.length > 0) {
                const solutionPromises = problemData.solutions.map(async (solution) => {
                    // Extract solution ID - could be an ID string or an object with _id/id
                    const solutionId = typeof solution === 'string' 
                        ? solution 
                        : (solution._id || solution.id);
                    
                    // Get pure text if solution is already populated
                    const pureText = typeof solution === 'object' && solution.text !== undefined
                        ? solution.text
                        : '';
                    
                    try {
                        // Always try to fetch formatted text
                        const solutionResponse = await userRequest.get(`/text/formatted/byText/${solutionId}?problemId=${problemId}`);
                        return {
                            id: solutionId,
                            text: solutionResponse.data.text || pureText,
                            formatted: solutionResponse.data.formatted !== false,
                            ...solutionResponse.data
                        };
                    } catch (error) {
                        console.error(`Error fetching formatted solution ${solutionId}:`, error);
                        // Fallback to pure text if formatted fetch fails
                        return {
                            id: solutionId,
                            text: pureText,
                            formatted: false
                        };
                    }
                });
                
                const fetchedSolutions = await Promise.all(solutionPromises);
                setSolutions(fetchedSolutions.filter(sol => sol !== null));
            } else {
                setSolutions([]);
            }

        } catch (error) {
            console.error('Error fetching problem:', error);
            setError('Failed to load problem. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [problemId]);

    const checkFavoriteStatus = useCallback(async () => {
        if (!problemId) return;
        
        try {
            const response = await userRequest.get(`/user/favorites/check/AlgorithmicProblem/${problemId}`);
            setIsFavorite(response.data.isFavorite);
        } catch (error) {
            console.error('Error checking favorite status:', error);
        }
    }, [problemId]);

    useEffect(() => {
        if (problemId) {
            fetchProblem();
            checkFavoriteStatus();
        }
    }, [problemId, fetchProblem, checkFavoriteStatus]);

    const handleToggleFavorite = async () => {
        if (!problemId || favoriteLoading) return;
        
        setFavoriteLoading(true);
        try {
            const response = await userRequest.post('/user/favorites/toggle', {
                contentType: 'AlgorithmicProblem',
                contentId: problemId
            });
            setIsFavorite(response.data.isFavorite);
        } catch (error) {
            console.error('Error toggling favorite:', error);
        } finally {
            setFavoriteLoading(false);
        }
    };

    const handleWordDisplayed = (word) => {
        // Only add words that have translation or grammar link
        if ((word.t && word.t.trim() !== '') || // word.translation
            (word.l && word.l.trim() !== '')) { // word.link
            
            setTappedWords(prevWords => {
                // Check if word already exists (avoid duplicates)
                const wordKey = word.w;
                const exists = prevWords.some(w => 
                    w.w === wordKey
                );
                
                if (!exists) {
                    // Add new word to the beginning of the array
                    return [word, ...prevWords];
                }
                return prevWords;
            });
        }
    };

    const handleSolveProblem = (url) => {
        window.open(url, '_blank');
    };

    const renderTextContent = (textData, onWordDisplayed) => {
        if (!textData) return null;

        return (
            <Box className={styles['problem-statement']}>
                {textData.formatted ? (
                    <AlgorithmicProblemInteractiveTextViewer
                        formattedText={textData.text}
                        textId={textData.id}
                        onWordDisplayed={onWordDisplayed}
                    />
                ) : (
                    <Box
                        className={styles['algorithm-text']}
                        dangerouslySetInnerHTML={{ __html: (textData.text || textData) }}
                    />
                )}
            </Box>
        );
    };

    const renderSolutionContent = (solutionData) => {
        if (!solutionData) return null;
        
        return (
            <Box className={styles['algorithm-text']}>
                {solutionData.formatted ? (
                    <AlgorithmicProblemInteractiveTextViewer
                        formattedText={solutionData.text}
                        textId={solutionData.id}
                        onWordDisplayed={handleWordDisplayed}
                    />
                ) : (
                    <Box
                        dangerouslySetInnerHTML={{ __html: (solutionData.text || '') }}
                    />
                )}
            </Box>
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                {error}
            </Alert>
        );
    }

    if (!problem) {
        return (
            <Alert severity="info" sx={{ mb: 3 }}>
                Problem not found.
            </Alert>
        );
    }

    const taskContent = formattedTaskText 
        ? renderTextContent(formattedTaskText, handleWordDisplayed)
        : (problem.task?.text ? (
            <Box className={styles['problem-statement']}>
                <Box
                    className={styles['algorithm-text']}
                    dangerouslySetInnerHTML={{ __html: (problem.task.text) }}
                />
            </Box>
        ) : 'Task not available.');

    // Combine all solutions into answer content
    const answerContent = solutions.length > 0 ? (
        <Box>
            {solutions.map((solution, index) => (
                <Box key={solution.id} className={styles['solution-block']}>
                    {renderSolutionContent(solution)}
                </Box>
            ))}
        </Box>
    ) : null;

    return (
        <Box sx={{ maxWidth: '1400px', margin: '0 auto', px: { xs: 1, sm: 2, md: 3 }, pb: 4 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                </IconButton>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" flexGrow={1}>
                    <Typography variant="h5" fontWeight="bold">
                        {problem.title || 'Algorithmic Problem'}
                    </Typography>
                    {problem.difficulty && (
                        <Chip
                            label={problem.difficulty.toUpperCase()}
                            size="small"
                            color={
                                problem.difficulty === 'easy' ? 'success' :
                                problem.difficulty === 'medium' ? 'warning' : 'error'
                            }
                        />
                    )}
                    {problem.problemUrl && (
                        <Typography 
                            variant="body2" 
                            component="a"
                            href={problem.problemUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main', textDecoration: 'none' }}
                        >
                            View on LeetCode →
                        </Typography>
                    )}
                </Box>
                <IconButton
                    onClick={handleToggleFavorite}
                    disabled={favoriteLoading}
                    sx={{ 
                        color: isFavorite ? 'secondary.main' : 'text.secondary',
                    }}
                >
                    {favoriteLoading ? (
                        <CircularProgress size={24} />
                    ) : isFavorite ? (
                        <Favorite />
                    ) : (
                        <FavoriteBorder />
                    )}
                </IconButton>
            </Box>

            {/* Main Content Area - One Long Scrollable Page */}
            <Box sx={{ 
                display: 'flex', 
                gap: 3,
                flexDirection: { xs: 'column', lg: 'row' }
            }}>
                {/* Main Content Column */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Task/Answer Viewer */}
                    <TaskAnswerViewer
                        taskContent={taskContent}
                        answerContent={answerContent}
                        onSolveProblem={handleSolveProblem}
                        solveProblemUrl={problem.problemUrl}
                    />
                </Box>

                {/* Sidebar - New Words Widget (always visible) */}
                <Box sx={{ 
                    width: { lg: '350px' },
                    display: { xs: 'none', lg: 'block' },
                    flexShrink: 0,
                    position: 'sticky',
                    top: 80,
                    alignSelf: 'flex-start'
                }}>
                    <TappedWordsWidget 
                        tappedWords={tappedWords}
                        variant="inline"
                    />
                </Box>
            </Box>

            {/* Mobile New Words Widget (always visible) */}
            <Box sx={{ display: { xs: 'block', lg: 'none' }, mt: 3 }}>
                <TappedWordsWidget 
                    tappedWords={tappedWords}
                    variant="inline"
                />
            </Box>
        </Box>
    );
};

AlgorithmicProblemView.propTypes = {
    problemId: PropTypes.string
};

export default AlgorithmicProblemView;
